import { utils, write } from 'xlsx';

const headers = {
  _id: 'ID',
  eventId: '赛事ID',
  eventName: '比赛名称',
  createdAt: '报名时间',
  name: '运动员姓名',
  sex: '性别',
  phone: '手机号',
  wechat: '微信号',
  weMediaId: '自媒体号',
  weMediaFans: '自媒体全网粉丝数',
  items: '报名项目',
  feeItems: '费用项目',
  orderNo: '报名订单号',
  enableCourse: '是否报名课程',
  school: '学校',
  grade: '年级',
  age: '年龄',
  idNumber: '证件号',
  idType: '证件类型',
  studentNumber: '学生证号',
  address: '地址',
  team: '代表战队',
  isPaid: '是否已支付',
  payableAmount: '应付金额',
  paymentAmount: '支付金额',
  totalPaymentAmount: '支付总金额（含后续兼项）',
  couponCode: '优惠码',
  couponName: '优惠券名称',
  originalPayableAmount: '优惠前金额',
  couponDiscountAmount: '优惠金额',
  couponUsedAt: '优惠码使用时间',
  payTime: '支付时间',
  paymentMode: '支付模式',
  subMchId: '特约商户号',
  transactionId: '微信支付订单号',
  isProfitSharing: '是否分账',
  profitSharingStatus: '分账状态',
  mainProfitSharingAmount: '主订单分润金额(元)',
  addonProfitSharingAmount: '兼项订单分润金额(元)',
  totalProfitSharingAmount: '订单分润总金额(元)',
  profitSharingReturnStatus: '分账回退状态',
  profitSharingReturnedAmount: '已回退金额(元)',
  profitSharingReturnNo: '分账回退单号',
  profitSharingNo: '分账单号',
  profitSharingResult: '分账响应结果',
  profitSharingReturnResult: '分账回退响应结果',
  remark: '管理员备注',
  eventStartTime: '比赛开始时间',
  eventEndTime: '比赛结束时间',
  updatedAt: '更新时间',
  height: '身高',
  weight: '体重'
};

const idTypeMap = {
  id_card: '身份证',
  hk_pass: '来往内地通行证',
  macao_pass: '来往内地通行证',
  taiwan_pass: '来往大陆通行证',
  hk_macao_residence_permit: '港澳居住证',
  taiwan_residence_permit: '台湾居住证',
  passport: '护照',
  foreign_residence_permit: '外国人居留证'
};

const profitSharingStatusMap = {
  none: '无',
  pending: '待分账',
  submitted: '已受理',
  processing: '分账中',
  success: '成功',
  finished: '已完结',
  failed: '失败'
};

const profitSharingReturnStatusMap = {
  none: '未回退',
  processing: '处理中',
  success: '已回退',
  failed: '失败'
};

const addonHeaders = {
  businessOrderNo: '追加单号',
  transactionId: '微信交易号',
  items: '追加项目',
  status: '订单状态',
  payableAmount: '应付金额(元)',
  paymentAmount: '实付金额(元)',
  paymentMode: '支付模式',
  subMchId: '特约商户号',
  isProfitSharing: '是否分账',
  platformFee: '平台分账金额(元)',
  profitSharingStatus: '分账状态',
  profitSharingNo: '分账单号',
  profitSharingResult: '分账响应详情',
  payTime: '支付时间',
  createdAt: '创建时间'
};

const addonManagementHeaders = {
  eventName: '赛事名称',
  athleteName: '运动员姓名',
  athletePhone: '手机号',
  baseOrderNo: '主报名订单号',
  businessOrderNo: '追加单号',
  transactionId: '微信交易号',
  items: '追加项目',
  paymentAmount: '实付金额(元)',
  paymentMode: '支付模式',
  subMchId: '特约商户号',
  isProfitSharing: '是否分账',
  platformFee: '平台分账金额(元)',
  profitSharingStatus: '分账状态',
  profitSharingNo: '分账单号',
  profitSharingResult: '分账响应详情',
  payTime: '支付时间',
  createdAt: '创建时间'
};

const addonStatusMap = {
  pending_payment: '待支付',
  payment_processing: '处理中',
  paid: '已完成',
  voided: '已作废',
  expired: '已过期',
  payment_exception: '同步异常'
};

function divideAmount(value) {
  return Number(value || 0) / 100;
}

function formatDate(dayjs, value, pattern) {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid && !parsed.isValid() ? '' : parsed.format(pattern);
}

export function getIdTypeName(idType) {
  return idTypeMap[idType] || '身份证';
}

export function sanitizeRegistrationFileName(name) {
  return String(name || '')
    .replace(/[\\/:*?"<>|]/g, '')
    .replace(/\s+/g, ' ')
    .trim() || '赛事';
}

export function formatBeijingDateCompact(date, fallback = '') {
  if (!date) return fallback;
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return fallback;
  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(parsed);
  const valueMap = parts.reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});
  return `${valueMap.year}${valueMap.month}${valueMap.day}`;
}

export function getRegistrationExportFileName(eventData = {}, dayjs) {
  const rawDate = eventData.endTime || eventData.eventEndTime;
  const fallbackDate = dayjs ? dayjs().format('YYYYMMDD') : '';
  const dateText = formatBeijingDateCompact(rawDate, fallbackDate);
  const eventName = sanitizeRegistrationFileName(eventData.eventName || '赛事');
  return `【报名名单】${dateText}-${eventName}.xlsx`;
}

export function buildRegistrationExcelBuffer(rows = [], dayjs) {
  const list = rows.map((data) => {
    const temp = { ...data };
    const itemsArray = [];
    (temp.items || []).forEach((item) => {
      itemsArray.push(`${item.item || ''}${item.group || ''}`);
    });
    temp.items = itemsArray.join('、');

    if (Array.isArray(temp.feeItems)) {
      const selectedFeeItems = temp.feeItems
        .filter(item => item.count > 0)
        .map(item => `${item.title}(${item.count}份)`);
      temp.feeItems = selectedFeeItems.join('、');
    }

    temp.sex = temp.sex ? '男' : '女';
    temp.isPaid = temp.isPaid ? '是✅' : '否❌';
    temp.idType = getIdTypeName(temp.idType);
    temp.payTime = formatDate(dayjs, temp.payTime, 'YYYY-MM-DD HH:mm:ss');
    temp.eventStartTime = formatDate(dayjs, temp.eventStartTime, 'YYYY-MM-DD');
    temp.eventEndTime = formatDate(dayjs, temp.eventEndTime, 'YYYY-MM-DD');
    const mainPaymentAmount = Number(temp.paymentAmount || 0);
    const addonPaymentAmount = Number(temp.addonPaymentAmount || 0);
    temp.payableAmount = divideAmount(temp.payableAmount);
    temp.paymentAmount = divideAmount(mainPaymentAmount);
    temp.totalPaymentAmount = divideAmount(mainPaymentAmount + addonPaymentAmount);
    temp.originalPayableAmount = temp.originalPayableAmount ? divideAmount(temp.originalPayableAmount) : '';
    temp.couponDiscountAmount = temp.couponDiscountAmount ? divideAmount(temp.couponDiscountAmount) : '';
    temp.couponUsedAt = temp.couponUsedAt ? formatDate(dayjs, temp.couponUsedAt, 'YYYY-MM-DD HH:mm:ss') : '';
    temp.paymentMode = temp.paymentMode === 'platform' ? '平台代收' : temp.paymentMode === 'service_provider' ? '服务商分账' : '未知';
    temp.isProfitSharing = temp.isProfitSharing ? '是' : '否';
    temp.profitSharingStatus = profitSharingStatusMap[temp.profitSharingStatus] || '未知';

    const mainProfitSharingAmount = Number(temp.mainProfitSharingAmount ?? temp.platformFee ?? 0);
    const addonProfitSharingAmount = Number(temp.addonProfitSharingAmount || 0);
    temp.mainProfitSharingAmount = divideAmount(mainProfitSharingAmount);
    temp.addonProfitSharingAmount = divideAmount(addonProfitSharingAmount);
    temp.totalProfitSharingAmount = divideAmount(
      temp.totalProfitSharingAmount ?? mainProfitSharingAmount + addonProfitSharingAmount
    );
    if (temp.profitSharingReturnedAmount) {
      temp.profitSharingReturnedAmount = divideAmount(temp.profitSharingReturnedAmount);
    }

    temp.profitSharingReturnStatus = profitSharingReturnStatusMap[temp.profitSharingReturnStatus || 'none'] || '未知';

    if (temp.profitSharingResult) {
      temp.profitSharingResult = JSON.stringify(temp.profitSharingResult);
    }
    if (temp.profitSharingReturnResult) {
      temp.profitSharingReturnResult = JSON.stringify(temp.profitSharingReturnResult);
    }

    return temp;
  });

  const worksheet = utils.json_to_sheet(list, { header: Object.keys(headers) });
  utils.sheet_add_aoa(worksheet, [Object.values(headers)], { origin: 'A1' });

  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, 'Sheet1');
  return write(workbook, { bookType: 'xlsx', type: 'array' });
}

export function saveAsExcelFile(buffer, fileName) {
  const data = new Blob([buffer], { type: 'application/octet-stream' });
  const link = document.createElement('a');
  link.href = window.URL.createObjectURL(data);
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(link.href);
}

export function exportRegistrationRows(rows = [], eventData = {}, dayjs) {
  const fileName = getRegistrationExportFileName(eventData, dayjs);
  const excelBuffer = buildRegistrationExcelBuffer(rows, dayjs);
  saveAsExcelFile(excelBuffer, fileName);
}

export function exportAddonRows(rows = [], baseOrder = {}, dayjs) {
  const list = rows.map((row) => ({
    businessOrderNo: row.businessOrderNo || '',
    transactionId: row.transactionId || '',
    items: (row.items || []).map(item => `${item.item || ''}${item.group || ''}`).join('、'),
    status: addonStatusMap[row.status] || row.status || '未知',
    payableAmount: divideAmount(row.payableAmount),
    paymentAmount: divideAmount(row.paymentAmount),
    paymentMode: row.paymentMode === 'platform' ? '平台代收' : row.paymentMode === 'service_provider' ? '服务商分账' : '未知',
    subMchId: row.subMchId || '',
    isProfitSharing: row.isProfitSharing ? '是' : '否',
    platformFee: divideAmount(row.platformFee),
    profitSharingStatus: profitSharingStatusMap[row.profitSharingStatus] || row.profitSharingStatus || '未知',
    profitSharingNo: row.profitSharingNo || '',
    profitSharingResult: row.profitSharingResult ? JSON.stringify(row.profitSharingResult) : '',
    payTime: formatDate(dayjs, row.payTime, 'YYYY-MM-DD HH:mm:ss'),
    createdAt: formatDate(dayjs, row.createdAt, 'YYYY-MM-DD HH:mm:ss')
  }));
  const worksheet = utils.json_to_sheet(list, { header: Object.keys(addonHeaders) });
  utils.sheet_add_aoa(worksheet, [Object.values(addonHeaders)], { origin: 'A1' });
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, '追加兼项订单');
  const eventName = sanitizeRegistrationFileName(baseOrder.eventName || '赛事');
  const athleteName = sanitizeRegistrationFileName(baseOrder.name || '运动员');
  const dateText = dayjs ? dayjs().format('YYYYMMDD') : '';
  const fileName = `【追加兼项订单】${eventName}-${athleteName}-${dateText}.xlsx`;
  saveAsExcelFile(write(workbook, { bookType: 'xlsx', type: 'array' }), fileName);
}

export function exportAddonManagementRows(rows = [], dayjs) {
  const list = rows.map((row) => ({
    eventName: row.eventName || '',
    athleteName: row.athleteName || '',
    athletePhone: row.athletePhone || '',
    baseOrderNo: row.baseOrderNo || '',
    businessOrderNo: row.businessOrderNo || '',
    transactionId: row.transactionId || '',
    items: (row.items || []).map(item => `${item.item || ''}${item.group || ''}`).join('、'),
    paymentAmount: divideAmount(row.paymentAmount || row.payableAmount),
    paymentMode: row.paymentMode === 'platform' ? '平台代收' : row.paymentMode === 'service_provider' ? '服务商分账' : '未知',
    subMchId: row.subMchId || '',
    isProfitSharing: row.isProfitSharing ? '是' : '否',
    platformFee: divideAmount(row.platformFee),
    profitSharingStatus: profitSharingStatusMap[row.profitSharingStatus] || row.profitSharingStatus || '未知',
    profitSharingNo: row.profitSharingNo || '',
    profitSharingResult: row.profitSharingResult ? JSON.stringify(row.profitSharingResult) : '',
    payTime: formatDate(dayjs, row.payTime, 'YYYY-MM-DD HH:mm:ss'),
    createdAt: formatDate(dayjs, row.createdAt, 'YYYY-MM-DD HH:mm:ss')
  }));
  const worksheet = utils.json_to_sheet(list, {
    header: Object.keys(addonManagementHeaders)
  });
  utils.sheet_add_aoa(worksheet, [Object.values(addonManagementHeaders)], {
    origin: 'A1'
  });
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, '兼项管理');
  const dateText = dayjs ? dayjs().format('YYYYMMDD') : '';
  saveAsExcelFile(
    write(workbook, { bookType: 'xlsx', type: 'array' }),
    `【兼项订单】${dateText}.xlsx`
  );
}
